import type { ReactNode } from "react"
import {
	IconCheckOutline18 as Check,
	IconGlobe2Outline18 as Globe,
} from "nucleo-ui-outline-18"
import Balancer from "react-wrap-balancer"

import { Badge, Button, Input } from "@productlane/ui"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

const HEADLINE = "Teach your agent"

function Slide({ children }: { children: ReactNode }) {
	return (
		<div className="mx-auto w-full max-w-[34rem] rounded-xl bg-white px-8 py-10 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="flex flex-col items-center text-center">{children}</div>
		</div>
	)
}

function Title({ subtitle }: { subtitle: string }) {
	return (
		<>
			<div className="mb-1 text-3xl font-bold tracking-tight">{HEADLINE}</div>
			<Balancer className="tracking-tight text-secondary">{subtitle}</Balancer>
		</>
	)
}

function SkipRow() {
	return (
		<Button
			variant="link"
			className="mt-1 w-full max-w-xxs opacity-70 hover:opacity-80"
		>
			{"I'll do this later"}
		</Button>
	)
}

// 1. Two cards, equal weight. Docs and repo are two ways into the same place.
function TwoCards() {
	return (
		<Slide>
			<Title subtitle="Your agent answers from what it knows. Give it your help center, your codebase, or both." />

			<div className="mt-8 grid w-full grid-cols-1 gap-3 text-left sm:grid-cols-2">
				<div className="flex flex-col rounded-lg bg-black/[0.02] p-4 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<Globe className="size-[18px] flex-none text-tertiary" />
					<div className="mt-3 text-[0.9rem] font-medium">Help center</div>
					<p className="mt-1 text-[13px] text-tertiary">
						Every page becomes an article the agent answers from.
					</p>
					<Input
						name="v1-url"
						className="mt-3"
						inputClassName="h-9 rounded-lg"
						placeholder="help.acme.com"
					/>
				</div>

				<div className="flex flex-col rounded-lg bg-black/[0.02] p-4 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<GitHubIcon className="size-[18px] flex-none" />
					<div className="mt-3 text-[0.9rem] font-medium">Your codebase</div>
					<p className="mt-1 text-[13px] text-tertiary">
						We read the repo once and write an article for every screen your
						customers reach.
					</p>
					<Button variant="outline" className="mt-3 h-9 w-full">
						Connect GitHub
					</Button>
				</div>
			</div>

			<p className="mt-4 max-w-md text-[13px] text-tertiary">
				Software teams resolve the most this way: the repo answers what nobody
				wrote down.
			</p>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

// 2. The field keeps the stage, the repo follows as the quieter second line.
function FieldThenRepo() {
	return (
		<Slide>
			<Title subtitle="Point us at your help center. Every page becomes an article your AI agent answers from." />

			<div className="mt-8 w-full text-left">
				<Input
					name="v2-url"
					icon={Globe}
					inputClassName="h-10 rounded-lg"
					placeholder="help.acme.com"
				/>
				<p className="mt-2 text-[13px] text-tertiary">
					Reading runs in the background. Every page goes live in Docs, ready to
					edit.
				</p>
			</div>

			<div className="mt-5 flex w-full items-center gap-3 rounded-lg border border-dashed border-gray-300 p-3 text-left dark:border-gray-700">
				<GitHubIcon className="size-[18px] flex-none" />
				<div className="min-w-0 flex-1">
					<div className="text-[13px] font-medium">Add your codebase</div>
					<p className="text-[13px] text-tertiary">
						Docs never cover every screen. Your repo does, and that is where the
						highest resolution rates come from.
					</p>
				</div>
				<Button variant="outline" size="sm" className="flex-none">
					Connect
				</Button>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

// 3. One question, two answers. The switcher keeps a single hero field.
function Switcher() {
	return (
		<Slide>
			<Title subtitle="Where should your agent learn from? Pick one now, add the other whenever you like." />

			<div className="mt-7 inline-flex rounded-lg bg-black/[0.03] p-1 dark:bg-gray-850">
				<button
					type="button"
					className="rounded-md bg-white px-3 py-1.5 text-[13px] font-medium shadow-sm dark:bg-gray-800"
				>
					Help center
				</button>
				<button
					type="button"
					className="rounded-md px-3 py-1.5 text-[13px] text-tertiary"
				>
					Codebase
				</button>
			</div>

			<div className="mt-5 w-full text-left">
				<Input
					name="v3-url"
					icon={Globe}
					inputClassName="h-10 rounded-lg"
					placeholder="help.acme.com"
				/>
				<p className="mt-2 text-[13px] text-tertiary">
					Every page becomes an article, live in Docs and ready to edit.
				</p>
			</div>

			<div className="mt-5 flex w-full items-start gap-2.5 rounded-lg bg-black/[0.02] p-3 text-left text-[13px] text-tertiary dark:bg-gray-850">
				<GitHubIcon className="mt-0.5 size-4 flex-none" />
				<span>
					On the codebase tab we read your repo once and write an agent only
					article for every screen. That is what lifts resolution for software
					teams, because the answer sits in the code long before anyone writes
					it down.
				</span>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

// 4. One card, two rows, each with its own control. Do one, do both, skip.
function StackedRows() {
	return (
		<Slide>
			<Title subtitle="Two sources, and the agent uses whatever it has. Most software teams end up giving it both." />

			<div className="mt-8 w-full divide-y divide-gray-200 rounded-lg text-left ring-1 ring-black/5 dark:divide-gray-700/80 dark:ring-white/10">
				<div className="flex items-center gap-3 p-4">
					<Globe className="size-[18px] flex-none text-tertiary" />
					<div className="min-w-0 flex-1">
						<div className="text-[0.9rem] font-medium">Help center</div>
						<p className="text-[13px] text-tertiary">
							What your team already wrote, in ten minutes.
						</p>
					</div>
					<Input
						name="v4-url"
						className="w-[13rem] flex-none"
						inputClassName="h-9 rounded-lg"
						placeholder="help.acme.com"
					/>
				</div>

				<div className="flex items-center gap-3 p-4">
					<GitHubIcon className="size-[18px] flex-none" />
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-2">
							<span className="text-[0.9rem] font-medium">Codebase</span>
							<Badge variant="green">Highest resolution</Badge>
						</div>
						<p className="text-[13px] text-tertiary">
							Every screen, every setting, straight from the repo.
						</p>
					</div>
					<Button variant="outline" className="h-9 flex-none">
						Connect GitHub
					</Button>
				</div>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

// 5. A checklist: the slide is done when either source is in, and it shows it.
function Checklist() {
	return (
		<Slide>
			<Title subtitle="Your agent resolves what it can read. Give it one source now, both when you have a minute." />

			<div className="mt-8 w-full space-y-2.5 text-left">
				<div className="flex items-center gap-3 rounded-lg bg-black/[0.02] p-3.5 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<span className="flex size-5 flex-none items-center justify-center rounded-full bg-green-500 text-white">
						<Check className="size-3 flex-none" />
					</span>
					<div className="min-w-0 flex-1">
						<div className="text-[0.9rem] font-medium">help.acme.com added</div>
						<p className="text-[13px] text-tertiary">
							30 pages are landing in Docs right now.
						</p>
					</div>
				</div>

				<div className="flex items-center gap-3 rounded-lg p-3.5 ring-1 ring-black/5 dark:ring-white/10">
					<span className="flex size-5 flex-none items-center justify-center rounded-full ring-1 ring-gray-300 dark:ring-gray-700" />
					<div className="min-w-0 flex-1">
						<div className="text-[0.9rem] font-medium">Connect your repo</div>
						<p className="text-[13px] text-tertiary">
							The agent answers from the code too, which is how software teams
							reach the highest resolution rates.
						</p>
					</div>
					<Button variant="outline" size="sm" className="flex-none">
						<GitHubIcon className="mr-1.5 size-4 flex-none" />
						Connect
					</Button>
				</div>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

const VARIANTS: { title: string; note: string; render: () => ReactNode }[] = [
	{
		title: "1. Two cards",
		note: "Docs and repo carry the same weight. Clearest when we want the repo chosen as often as the docs.",
		render: TwoCards,
	},
	{
		title: "2. Field first, repo under it",
		note: "Closest to the slide that ships today. The repo is an offer, not a second decision.",
		render: FieldThenRepo,
	},
	{
		title: "3. Switcher",
		note: "One hero field at a time. Keeps the slide calm, hides the second source behind a tab.",
		render: Switcher,
	},
	{
		title: "4. Two rows in one card",
		note: "Both are actionable without leaving the row. The badge carries the resolution claim.",
		render: StackedRows,
	},
	{
		title: "5. Checklist",
		note: "Shows progress: one source in, one to go. Reads as setup rather than as a question.",
		render: Checklist,
	},
]

export function OnboardingKnowledgePage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-dvh overflow-y-auto bg-gray-100 pb-24 dark:bg-gray-950">
			<div className="mx-auto max-w-4xl px-6 py-12">
				<h1 className="text-2xl font-semibold tracking-tight">
					Onboarding: help center and codebase
				</h1>
				<p className="mt-2 max-w-2xl text-[15px] text-secondary">
					Five ways to put the GitHub step on the slide that asks for a help
					center URL. Every variant carries a line that says why the repo lifts
					resolution.
				</p>

				<div className="mt-10 space-y-16">
					{VARIANTS.map((variant) => (
						<section key={variant.title}>
							<div className="mb-4">
								<div className="text-[0.95rem] font-medium">
									{variant.title}
								</div>
								<p className="text-[13px] text-tertiary">{variant.note}</p>
							</div>
							{variant.render()}
						</section>
					))}
				</div>
			</div>
		</div>
	)
}
